<template>
  <div class="fts-dashboard-widget" :class="widgetClass">
    <!-- title -->
    <div class="d-widget-title">
      <div class="activity-stream-title" v-if="activityStreamViewMode">
        <div class="title-label">{{widget.name}}</div>
        <div class="mode-selection">
          <font-awesome-icon :icon="['fas', 'user']" class="mode-selection-icon" />
          <div class="mode-option" @click="showActivityIndividual" :class="{'active': !activityEveryone}">me</div>
          <div class="mode-option" @click="showActivityEveryone" :class="{'active': activityEveryone}">everyone</div> 
        </div>
      </div>
      <div v-else>{{widget.name}}</div>
    </div>
    <!-- activity stream -->
    <activity-stream 
      :widget="widget"
      :view-data="dashboardData[widget.fieldName]"
      :loading="loading" 
      @open="onClick"
      @activityEveryone="showActivityEveryone"
      @activityIndividual="showActivityIndividual"
      v-if="widget.template === 'activityStream'"
    />
    <!-- calendar event -->
    <calendar-event
      :widget="widget"
      :view-data="dashboardData"
      :loading="loading"
      @open="onClick"
      v-else-if="widget.template === 'calendarEvent'"
    />
    <!-- locus -->
    <locus 
      :widget="widget"
      :view-data="dashboardData"
      :loading="loading"
      @open="onClick"
      v-else-if="widget.template === 'locus'"
    />
    <!-- workspace stream -->
    <workspace-history 
      :widget="widget"
      :view-data="dashboardData[widget.fieldName]"
      :loading="loading" 
      @open="onClick"
      v-else-if="widget.template === 'workspaceHistory'"
    />
    <!-- studio summary -->
    <studio-summary 
      :widget="widget"
      :view-data="dashboardData[widget.fieldName]"
      :loading="loading" 
      v-else-if="widget.template === 'workspaceSummary'"
    />
    <!-- default -->
    <div @click="onClick" v-else>
      Click Me
    </div>
  </div>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';

import ActivityStream from "./widgets/ActivityStream";
import CalendarEvent from "./widgets/CalendarEvent";
import Locus from "./widgets/Locus";
import workspaceHistory from "./widgets/workspaceHistory";
import StudioSummary from "./widgets/StudioSummary";

export default {
  props: {
    widget: {
      type: Object,
      default: () => ({}),
    },
    dashboardData: {
      type: Object,
      default: () => ({}),
    },
    activityEveryone: Boolean,
    loading: Boolean,
  },
  components: {
    "activity-stream": ActivityStream,
    "calendar-event": CalendarEvent,
    "locus": Locus,
    "workspace-history": workspaceHistory,
    "studio-summary": StudioSummary,
  },
  methods: {
    onClick(event) {
      this.$emit("widget:click", {
        widgetId: this.widget.id,
        ...event,
      });
    },
    showActivityEveryone() {
      if (!this.activityEveryone)
        this.$emit("activity:everyone");
    },
    showActivityIndividual() {
      if (this.activityEveryone)
        this.$emit("activity:individual");
    },
  },
  computed: {
    widgetClass() {
      switch(this.widget.template) {
        case "activityStream":
          return "activity-stream"
        default:
          return "default"
      }
    },
    activityStreamViewMode() {
      return this.widget && this.widget.template === 'activityStream' && this.widget.viewMode && authorizationStore.getters.isAdminUser;
    }
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-dashboard-widget {
  margin: 1rem;
  &.default {
    .d-widget-content {
      padding: .75rem 0;
      border-radius: .575rem
    }
  }
  .d-widget-title {
    margin-bottom: .575rem;
    color: var(--fts-text);
  }

  // wip
  .activity-stream-title {
    display: flex;
    align-items: center;
    .title-label {
      @include text-overflow-ellipsis;
      flex: 1;
      color: var(--fts-text);
    }
    .mode-selection {
      display: flex;
      align-items: center;
      margin-left: .75rem;
      .mode-selection-icon {
        margin-right: .375rem;
        font-size: .875rem;
        color: var(--fts-gray-5);
      }
      .mode-option {
        @include user-select-none;
        position: relative;
        padding: .135rem .375rem;
        font-size: .875rem;
        color: var(--fts-gray-5);
        cursor: pointer;
        &.active {
          color: $primaryColor;
        }
        &:not(:last-child) {
          &::after {
            content: "";
            position: absolute;
            right: 0;
            top: .275rem;
            bottom: .275rem;
            border-right: 1px solid var(--fts-gray-5);
          }
        }
      }
    }
  }


}

// d locus card
.d-locus-card-wrapper {
  max-height: 350px;
  background: inherit;
  & + .d-locus-card-wrapper {
    margin-top: calc(2rem - 0.75rem);
  }
  .d-locus-card-header {
    position: sticky;
    top: 0;
    padding: 0 1rem .375rem;
    background: inherit;
    z-index: 10;
  }
  .d-locus-card-title {
    font-size: 1rem;
  }
  .d-locus-card-description {
    &:not(:first-child) {
      margin-top: .175rem;
    }
    font-size: .75rem;
    opacity: .5;
  }
  .d-locus-card-content {
    margin-left: .575rem;
    margin-right: .575rem;
    padding: .575rem;
    border: 1px solid var(--fts-gray-3);
    border-radius: .575rem;
    background: var(--fts-background);
    color: var(--fts-text);
    cursor: pointer;
    & + .d-locus-card-content {
      margin-top: .375rem;
    }
    .d-calendar-event-row-alpha {
      min-width: 0;
      display: flex;
      align-items: flex-start;
      margin-bottom: .375rem;
      .d-calendar-event-row-alpha-l {
        @mixin default {
          font-size: $fontSize;
          font-weight: 600;
          line-height: $lineHeight;
        }
        $lineHeight: 1.25;
        $fontSize: 1rem;
        min-width: 0;
        flex: 1 1 60%;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        margin-top: auto;
        margin-bottom: auto;
        .d-calendar-event-date {
          @include default;
          margin-right: .75rem;
        }
        .d-calendar-event-date-field {
          @include default;
          min-width: 0;
          display: flex;
          align-items: center;
          margin-right: .375rem;
          > div {
            @include text-overflow-ellipsis;
          }
        }
        .d-title {
          @include text-line-clamp($fontSize: $fontSize, $lineHeight: $lineHeight, $lineClamp: 2);
          font-weight: 500;
        }
      }
      .d-state {
        margin-left: .75rem;
      }
      .d-assign {
        margin-left: .75rem;
      }
    }
    .d-locus-row-alpha {
      min-width: 0;
      display: flex;
      align-items: flex-start;
      margin-bottom: .375rem;
      .d-title {
        @include text-line-clamp($fontSize: 1rem, $lineHeight: 1.25, $lineClamp: 2);
        flex: 1 1 60%;
        margin-top: auto;
        margin-bottom: auto;
        font-weight: 500;
      }
      .d-state {
        margin-left: .75rem;
      }
      .d-assign {
        margin-left: .75rem;
      }
    }
    .d-locus-row-beta {
      min-width: 0;
      display: flex;
      align-items: center;
      margin-bottom: .275rem;
      .d-locus-row-beta-l {
        flex: 1 1 60%;
        min-width: 0;
        display: flex;
        align-items: center;
        .d-document-id {
          margin-right: .75rem;
        }
      }
      .d-audit {
        margin-left: .75rem;
        &.audit-created {
          .d-audit-profile {
            max-width: 60px;
          }
        }
      }
    }
    .d-locus-row-gamma {
      min-width: 0;
      display: flex;
      align-items: center;
      .d-locus-row-gamma-l {
        flex: 1 1 50%;
        min-width: 0;
        display: flex;
        align-items: center;
      }
      .d-due {
        margin-left: .75rem;
      }
    }
  }
  .d-locus-no-result {
    padding: 1rem;
    font-size: .875rem;
    text-align: center;
  }
}
// document id
.d-document-id {
  font-size: .75rem;
  color: var(--fts-gray-5);
}
.d-module {
  font-size: .75rem;
}
// audit
.d-audit {
  min-width: 0;
  display: flex;
  align-items: center;
  font-size: .75rem;
}
.d-audit-by {
  margin-right: .375rem;
}
.d-audit-profile + .d-audit-date {
  margin-left: .375rem;
}
.d-audit-profile {
  color: var(--fts-gray-5);
}
.d-audit-date {
  font-size:.75rem;
  white-space: nowrap;
  cursor: default;
  color: var(--fts-gray-5);
}
// collaboration
.d-collaboration {
  font-size: .875rem;
}
// assign
.d-assign {
  min-width: unset;
}
// due
.d-due {
  display: inline-flex;
  font-size: .875rem;
  cursor: default;
  &.overdue {
    color: var(--fts-danger);
  }
}
</style>